import { Trash } from '@griply/assets';
import { GoalType } from '@griply/types';
import { hasGoalParent, isInternalUser } from '@griply/utils';
import {
  useArchiveGoal,
  useCreateUnlimitedGoalsAllowed,
  useDuplicateGoal,
} from 'features/goals';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate } from 'react-router';
import {
  ArchiveDocumentsBoxBig,
  CopyDuplicateObject,
  Upload,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenCreateGoalTemplateDialog } from 'shared/contexts/create-goal-template';
import { useOpenDeleteGoalDialog } from 'shared/contexts/delete-goal';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes/paths';
import { Goal } from 'shared/types/goal';
import { getGoalDetailUrl } from 'shared/utils/get-goal-detail-url';
import { ContextMenuOptions } from 'shared/views/goal-filter-list/types';

export type ManageListProps = {
  goal: Goal;
  onClose: () => void;
  excludedOptions?: ContextMenuOptions[];
};

export const ManageList: React.FC<ManageListProps> = ({
  goal,
  onClose,
  excludedOptions,
}) => {
  const { t } = useTranslation();
  const links = useEntityLinks();
  const user = useUser();
  const goalDetailMatch = useMatch(Paths.GoalDetail);
  const projectDetailMatch = useMatch(Paths.ProjectDetail);
  const isOnDetailView = goalDetailMatch ?? projectDetailMatch;

  const openTemplateDialog = useOpenCreateGoalTemplateDialog();
  const openDeleteGoalDialog = useOpenDeleteGoalDialog();
  const archiveGoal = useArchiveGoal();
  const navigate = useNavigate();
  const duplicateGoal = useDuplicateGoal();
  const openPremiumDialog = useOpenPremiumDialog();
  const { allowed: duplicateAllowed } = useCreateUnlimitedGoalsAllowed(
    goal.type ?? GoalType.Goal,
  );

  const onDuplicateGoal = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!duplicateAllowed) {
      openPremiumDialog();
      onClose();
      return;
    }
    const newGoalId = await duplicateGoal(goal);

    if (newGoalId) {
      navigate(getGoalDetailUrl({ id: newGoalId, type: goal.type }));
    }

    onClose();
  };

  const onCreateTemplate = (e: React.MouseEvent) => {
    e.preventDefault();
    openTemplateDialog(goal.id);
    onClose();
  };

  const onDeleteGoal = (e: React.MouseEvent) => {
    e.preventDefault();
    openDeleteGoalDialog(
      goal,
      isOnDetailView?.params.id === goal.id ? Paths.Goals : undefined,
    );
    onClose();
  };

  const onArchiveGoal = (e: React.MouseEvent) => {
    e.preventDefault();
    archiveGoal(goal);
  };

  return (
    <PopupMenuList>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onDuplicateGoal}
          isPremium={!duplicateAllowed}
          start={<Icon icon={CopyDuplicateObject} />}
        >
          {t('context-menus.goal.labels.duplicate-goal')}
        </PopupMenuButton>
      </PopupMenuListItem>
      {isInternalUser(user?.email) && !hasGoalParent(goal, { links }) && (
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onCreateTemplate}
            startColor={Colors.Info}
            start={<Icon icon={Upload} />}
            textColor={Colors.Info}
          >
            {t('context-menus.goal.labels.create-template')}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}
      {!excludedOptions?.includes(ContextMenuOptions.Archive) && (
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onArchiveGoal}
            start={<Icon icon={ArchiveDocumentsBoxBig} />}
          >
            {t(
              !!goal.archivedAt
                ? 'context-menus.goal.labels.de-archive'
                : 'context-menus.goal.labels.archive',
            )}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}
      {!excludedOptions?.includes(ContextMenuOptions.Delete) && (
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onDeleteGoal}
            start={<Icon icon={Trash} />}
            startColor={Colors.Error}
            textColor={Colors.Error}
          >
            {t('context-menus.goal.labels.delete')}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}
    </PopupMenuList>
  );
};
